import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Button,
    FormControl,
    FormHelperText,
    FormLabel,
    Text,
    Textarea,
    VStack,
} from '@chakra-ui/react';
import React, { useRef, useState } from 'react';
import { translate } from '../../../i18n';
import { EventParticipantDto } from '../../../models/event/dto/event-participant.dto';
import { useKickEventParticipant } from '../hooks/useKickEventParticipant';

interface KickEventParticipantModalProps {
    isOpen: boolean;
    onClose: () => void;
    eventId: number;
    participant: EventParticipantDto | null;
}

const KickEventParticipantModal: React.FC<KickEventParticipantModalProps> = ({
    isOpen,
    onClose,
    eventId,
    participant,
}) => {
    const cancelRef = useRef<HTMLButtonElement>(null);
    const kickParticipantMutation = useKickEventParticipant();
    const [reason, setReason] = useState('');

    const handleClose = () => {
        setReason('');
        onClose();
    };

    const handleConfirmKick = () => {
        if (!participant) return;

        kickParticipantMutation.mutate({
            eventId,
            participantUserId: participant.participant.id,
            reason: reason.trim() || undefined,
        }, {
            onSettled: handleClose,
        });
    };

    if (!participant) return null;

    return (
        <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={handleClose}
            isCentered
        >
            <AlertDialogOverlay>
                <AlertDialogContent maxW="500px">
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                        {translate('event.remove_participant_title') || 'Remove Participant'}
                    </AlertDialogHeader>

                    <AlertDialogBody>
                        <VStack spacing={4} align="stretch">
                            <div>
                                {translate('event.remove_participant_message') || 'Are you sure you want to remove'}{' '}
                                <strong>
                                    {participant.participant.firstName} {participant.participant.lastName}
                                </strong>?
                            </div>

                            <FormControl>
                                <FormLabel>
                                    {translate('event.kick_reason_label') || 'Reason (Optional)'}
                                </FormLabel>
                                <Textarea
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder={translate('event.kick_reason_placeholder') || 'Enter the reason for removing this participant...'}
                                    rows={4}
                                />
                                <FormHelperText>
                                    {translate('event.kick_reason_helper') || 'This reason will be stored for reference and audit purposes.'}
                                </FormHelperText>
                            </FormControl>

                            <Text fontSize="sm" color="gray.500">
                                {translate('event.remove_participant_refund_info') || 'This action will refund the participant if they have paid to join this event. This action cannot be undone.'}
                            </Text>
                        </VStack>
                    </AlertDialogBody>

                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={handleClose}>
                            {translate('common.cancel') || 'Cancel'}
                        </Button>
                        <Button
                            colorScheme="red"
                            onClick={handleConfirmKick}
                            ml={3}
                            isLoading={kickParticipantMutation.isLoading}
                        >
                            {translate('event.remove_participant') || 'Remove Participant'}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
    );
};

export default KickEventParticipantModal;
